import { FileSourceEnum } from '@systemeio/file-manager';
import { PictureIcon, RecaptchaIcon, SettingsIcon } from '@systemeio/ui/icons';
import { FieldErrorAndDescription, FormInput, Hint, Modal, NeutralButton, PrimaryButton, toast, Tooltip } from '@systemeio/ui';
import { BadRequest } from '@systemeio/util';
import { InternalError } from '@systemeio/util';
import { editDomain } from 'modules/profile/custom-domains-settings/api/custom-domain-api';
import DomainLoginPageButton from 'modules/profile/custom-domains-settings/components/domain-login-page-button';
import DomainNotFoundPageButton from 'modules/profile/custom-domains-settings/components/domain-not-found-page-button';
import { CustomDomainsStatusEnum } from 'modules/profile/custom-domains-settings/enums/custom-domains-settings-enum';
import { CustomDomainErrorResponseContentInterface } from 'modules/profile/custom-domains-settings/types/custom-domain-errors-interface';
import { CustomDomainsEditErrorsInterface, CustomDomainsEditInterface, CustomDomainsSettingsInterface, DomainLabelTypeEnum } from 'modules/profile/custom-domains-settings/types/custom-domains-settings-interface';
import React, { useEffect, useState } from 'react';
import ConfirmationModal from 'shared/components/confirmation-modal';
import { ImageUploaderWithPreviewImage } from 'shared/components/file-uploader/image-uploader-with-preview-image';
import TransLoco from 'shared/components/trans-loco';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import { testGroupDomains } from 'modules/profile/custom-domains-settings/constants/test-group-domains';
import { DomainWebsiteLoginPageButton } from 'modules/profile/custom-domains-settings/components/domain-website-login-page-button';
interface DomainSettingsModalInterface {
  opened: boolean;
  onClose: () => void;
  domain: CustomDomainsSettingsInterface;
  onRemoveLoginPage: (domain: CustomDomainsSettingsInterface) => Promise<void>;
  editDomainWithUpdate: (data: CustomDomainsSettingsInterface) => Promise<void>;
  onRemoveNotFoundPage: (domain: CustomDomainsSettingsInterface) => Promise<void>;
}
const defaultValues: CustomDomainsEditInterface = {
  domainCaptchaSettings: {
    openKey: '',
    privateKey: ''
  },
  facebookConversionPixelSettings: {
    pixel: '',
    token: ''
  },
  emailForSupport: null,
  seo: {
    fbAppId: ''
  }
};
const defaultErrors: CustomDomainsEditErrorsInterface = {
  facebookConversionPixelSettings: {
    pixel: '',
    token: ''
  },
  faviconFile: '',
  logoFile: '',
  common: '',
  domainCaptchaSettings: {
    openKey: '',
    privateKey: ''
  },
  emailForSupport: '',
  seo: {
    fbAppId: ''
  }
};
const DomainSettingsModal = ({
  domain,
  opened,
  onClose,
  onRemoveLoginPage,
  editDomainWithUpdate,
  onRemoveNotFoundPage
}: DomainSettingsModalInterface) => {
  const {
    t
  } = useLocoTranslation();
  const [isFetching, setIsFetching] = useState(false);
  const [tempState, setTempState] = useState<CustomDomainsEditInterface>(defaultValues);
  const [errors, setErrors] = useState<CustomDomainsEditErrorsInterface>(defaultErrors);
  const [isDeleteLoginOpen, setIsDeleteLoginOpen] = useState(false);
  const [isDeleteNotFoundOpen, setIsDeleteNotFoundOpen] = useState(false);
  const isWhiteLabelDomain = domain.labelType === DomainLabelTypeEnum.WhiteLabel;
  useEffect(() => {
    setTempState(prev => ({
      ...prev,
      logoFile: isWhiteLabelDomain ? undefined : domain.logoFile,
      faviconFile: isWhiteLabelDomain ? undefined : domain.faviconFile,
      emailForSupport: domain.emailForSupport,
      seo: domain.seo ? {
        fbAppId: domain.seo.fbAppId
      } : {
        fbAppId: ''
      },
      facebookConversionPixelSettings: domain.facebookConversionPixelSettings ? {
        pixel: domain.facebookConversionPixelSettings.pixel,
        token: domain.facebookConversionPixelSettings.token
      } : {
        pixel: '',
        token: ''
      },
      domainCaptchaSettings: domain.domainCaptchaSettings ? {
        privateKey: domain.domainCaptchaSettings.privateKey,
        openKey: domain.domainCaptchaSettings.openKey
      } : {
        privateKey: '',
        openKey: ''
      }
    }));
  }, [domain, opened, isWhiteLabelDomain]);
  const onEdit = async () => {
    try {
      setIsFetching(true);
      setErrors(defaultErrors);
      await editDomain(domain.id, {
        ...tempState,
        logoFile: tempState.logoFile === null ? null : tempState.logoFile?.id,
        faviconFile: tempState.faviconFile === null ? null : tempState.faviconFile?.id
      });
      const newData: CustomDomainsSettingsInterface = {
        ...domain,
        ...tempState
      };
      await editDomainWithUpdate(newData);
      setIsFetching(false);
      onClose();
    } catch (e) {
      setIsFetching(false);
      if (e instanceof BadRequest) {
        const errors_0 = e.errors as unknown as CustomDomainErrorResponseContentInterface;
        if (errors_0.common) {
          const common = errors_0.common;
          setErrors(prev_0 => ({
            ...prev_0,
            common: common.join('')
          }));
        }
        if (errors_0.fields) {
          const fields = errors_0.fields;
          setErrors(prev_1 => ({
            ...prev_1,
            logoFile: fields.logoFile?.join(''),
            domainCaptchaSettings: fields.domainCaptchaSettings ? {
              openKey: fields.domainCaptchaSettings.openKey?.join(''),
              privateKey: fields.domainCaptchaSettings.privateKey?.join('')
            } : prev_1.domainCaptchaSettings,
            facebookConversionPixelSettings: {
              pixel: fields.facebookConversionPixelSettings?.pixel?.join(''),
              token: fields.facebookConversionPixelSettings?.token?.join('')
            },
            faviconFile: fields.faviconFile?.join(''),
            emailForSupport: fields.emailForSupport?.join(''),
            seo: {
              fbAppId: fields?.seo?.fbAppId.join('')
            }
          }));
        }
      } else if (e instanceof InternalError) {
        setErrors(prev_2 => ({
          ...prev_2,
          common: t('core.error.internal_error_message')
        }));
      } else {
        toast.error(t('global.error'));
      }
    }
  };
  const onModalClose = () => {
    setTempState(defaultValues);
    setErrors(defaultErrors);
  };
  const faviconLabel = <div className={'flex gap-1.5'}>
      <PictureIcon className={'h-[18px] w-[18px]'} />
      <Hint label={t('dashboard.settings.custom_domains.favicon_description')}>
        <span>{t('dashboard.settings.custom_domains.favicon_settings')}</span>
      </Hint>
    </div>;
  const logoLabel = <div className={'flex gap-1.5'}>
      <PictureIcon className={'h-[18px] w-[18px]'} />
      <Hint label={t('dashboard.settings.custom_domains.logo_description')}>
        <span>{t('global.logo')}</span>
      </Hint>
    </div>;
  return <>
      <Modal opened={opened} onClose={onClose} title={t('dashboard.settings.custom_domains.domain_settings')} isFetching={isFetching} afterLeave={onModalClose}>
        <form className="flex flex-col gap-6 lg:gap-10">
          <div className="flex flex-col gap-5">
            {domain.status === CustomDomainsStatusEnum.active && <>
                <div className={'flex flex-col gap-2'}>
                  <div className={'flex gap-1.5'}>
                    <SettingsIcon className={'h-[18px] w-[18px]'} fill="rgb(var(--fill-primary))" />
                    <Hint label={t('dashboard.settings.custom_domains.customize_login_page')}>
                      <span>{t('dashboard.settings.custom_domains.login_page_settings')}</span>
                    </Hint>
                  </div>
                  <div className={'flex gap-2'}>
                    {testGroupDomains.includes(domain.domainName) ? <DomainWebsiteLoginPageButton domainName={domain.domainName} domainId={domain.id} isWhiteLabelDomain={isWhiteLabelDomain} /> : <Tooltip popoverClassName="!max-w-[300px]" label={t('dashboard.settings.custom_domains.cant_edit_page_warning')} disabled={!isWhiteLabelDomain}>
                        <DomainLoginPageButton domainId={domain.id} disabled={isWhiteLabelDomain} />
                      </Tooltip>}
                    {!testGroupDomains.includes(domain.domainName) && domain.loginPage?.id && !isWhiteLabelDomain && <NeutralButton onClick={(e_0: React.MouseEvent<HTMLButtonElement>) => {
                  e_0.preventDefault();
                  setIsDeleteLoginOpen(true);
                }} className={'hover:border-danger hover:text-danger'}>
                          {t('dashboard.settings.custom_domains.login_page_restore')}
                        </NeutralButton>}
                  </div>
                </div>
                <hr className={'text-text-secondary/30'} />
                <div className={'flex flex-col gap-2'}>
                  <div className={'flex gap-1.5'}>
                    <SettingsIcon className={'h-[18px] w-[18px]'} fill="rgb(var(--fill-primary))" />
                    <Hint label={t('dashboard.settings.custom_domains.customize_not_found_page')}>
                      <span>{t('dashboard.settings.custom_domains.not_found_page_settings')}</span>
                    </Hint>
                  </div>
                  <div className={'flex gap-2'}>
                    <Tooltip popoverClassName="!max-w-[300px]" label={t('dashboard.settings.custom_domains.cant_edit_page_warning')} disabled={!isWhiteLabelDomain}>
                      <DomainNotFoundPageButton domainId={domain.id} disabled={isWhiteLabelDomain} />
                    </Tooltip>
                    {domain.notFoundPage?.id && !isWhiteLabelDomain && <NeutralButton onClick={(e_1: React.MouseEvent<HTMLButtonElement>) => {
                  e_1.preventDefault();
                  setIsDeleteNotFoundOpen(true);
                }} className={'hover:border-danger hover:text-danger'}>
                        {t('dashboard.settings.custom_domains.not_found_page_restore')}
                      </NeutralButton>}
                  </div>
                </div>
                <hr className={'text-text-secondary/30'} />
              </>}
            <div className={'flex flex-col gap-2'}>
              {isWhiteLabelDomain ? <>
                  {faviconLabel}
                  <FieldErrorAndDescription descriptionClassName="opacity-100 text-text-tertiary/70" description={<TransLoco t={t} i18nKey={'dashboard.settings.custom_domains.favicon_configurable_in_wl_description'} components={{
                a: <LinkWithoutPrefetch href={'/agency-center/white-label-settings'} />
              }} />} />
                </> : <ImageUploaderWithPreviewImage label={faviconLabel} previewClassName="h-[30px] w-[30px]" value={tempState.faviconFile} onRemove={() => setTempState(prev_3 => {
              return {
                ...prev_3,
                faviconFile: null
              };
            })} onSelect={dataFile => setTempState(prev_4 => ({
              ...prev_4,
              faviconFile: dataFile
            }))} error={errors.faviconFile} source={FileSourceEnum.Settings} aspectRatio={1} allowSwitchingCroppingShape />}
            </div>
            <hr className={'text-text-secondary/30'} />
            <div className={'flex flex-col gap-2'}>
              {isWhiteLabelDomain ? <>
                  {logoLabel}
                  <FieldErrorAndDescription descriptionClassName="opacity-100 text-text-tertiary/70" description={<TransLoco t={t} i18nKey={'dashboard.settings.custom_domains.logo_configurable_in_wl_description'} components={{
                a: <LinkWithoutPrefetch href={'/agency-center/white-label-settings'} />
              }} />} />
                </> : <ImageUploaderWithPreviewImage label={logoLabel} value={tempState.logoFile} onRemove={() => setTempState(prev_5 => {
              return {
                ...prev_5,
                logoFile: null
              };
            })} aspectRatio={1} allowSwitchingCroppingShape onSelect={file => setTempState(prev_6 => ({
              ...prev_6,
              logoFile: file
            }))} source={FileSourceEnum.Settings} error={errors.logoFile} />}
            </div>
            <hr className={'text-text-secondary/30'} />
            <div className={'flex flex-col gap-2'}>
              <div className={'flex gap-1.5'}>
                <Hint label={t('dashboard.settings.custom_domains.tooltip_contact_us_email')}>
                  <span>{t('dashboard.settings.custom_domains.title_contact_us_email')}</span>
                </Hint>
              </div>
              <div className={'[&>*]:flex-1 flex flex-col gap-2 lg:flex-row lg:gap-10'}>
                <FormInput value={tempState.emailForSupport || ''} onChange={e_2 => setTempState(prev_7 => ({
                ...prev_7,
                emailForSupport: e_2.target.value
              }))} error={errors?.emailForSupport} />
              </div>
            </div>
            <hr className={'text-text-secondary/30'} />
            <div className={'flex flex-col gap-2'}>
              <div className={'flex gap-1.5'}>
                <RecaptchaIcon className={'h-[18px] w-[18px]'} />
                <Hint label={t('dashboard.settings.custom_domains.captcha_descriptions')}>
                  <span>{t('dashboard.settings.custom_domains.captcha_settings')}</span>
                </Hint>
              </div>
              <span>
                <TransLoco t={t} i18nKey={'dashboard.settings.custom_domains.captcha_text'} components={{
                a: <a className="primary-link" href={`https://www.google.com/recaptcha/about/`} target={'_blank'} rel={'noreferrer'} />
              }} />
              </span>
              <div className={'[&>*]:flex-1 flex flex-col gap-2 lg:flex-row lg:gap-10'}>
                <FormInput value={tempState.domainCaptchaSettings.openKey} onChange={e_3 => setTempState(prev_8 => ({
                ...prev_8,
                domainCaptchaSettings: {
                  ...prev_8.domainCaptchaSettings,
                  openKey: e_3.target.value
                }
              }))} label={t('dashboard.settings.custom_domains.captcha_site_key')} error={errors.domainCaptchaSettings.openKey} />
                <FormInput value={tempState.domainCaptchaSettings.privateKey} onChange={e_4 => setTempState(prev_9 => ({
                ...prev_9,
                domainCaptchaSettings: {
                  ...prev_9.domainCaptchaSettings,
                  privateKey: e_4.target.value
                }
              }))} label={t('dashboard.settings.custom_domains.captcha_secret_key')} error={errors.domainCaptchaSettings.privateKey} />
              </div>
            </div>
            <hr className={'text-text-secondary/30'} />
            <div className={'flex flex-col gap-2'}>
              <span>{t('dashboard.settings.custom_domains.facebook_conversion')}</span>
              <span>
                <TransLoco t={t} i18nKey={'dashboard.settings.custom_domains.facebook_description'} components={{
                a: <a className="primary-link" href={`https://www.facebook.com/events_manager2`} target={'_blank'} rel={'noreferrer'} />
              }} />
              </span>
              <div className={'[&>*]:flex-1 flex flex-col gap-2 lg:flex-row lg:gap-10'}>
                <FormInput value={tempState.facebookConversionPixelSettings.pixel} onChange={e_5 => setTempState(prev_10 => ({
                ...prev_10,
                facebookConversionPixelSettings: {
                  ...prev_10.facebookConversionPixelSettings,
                  pixel: e_5.target.value
                }
              }))} label={t('dashboard.settings.custom_domains.facebook_pixel_id')} error={errors.facebookConversionPixelSettings.pixel} />
                <FormInput value={tempState.facebookConversionPixelSettings.token} onChange={e_6 => setTempState(prev_11 => ({
                ...prev_11,
                facebookConversionPixelSettings: {
                  ...prev_11.facebookConversionPixelSettings,
                  token: e_6.target.value
                }
              }))} label={t('dashboard.settings.custom_domains.facebook_access_token')} error={errors.facebookConversionPixelSettings.token} />
              </div>
              <FormInput value={tempState.seo.fbAppId} onChange={e_7 => setTempState(prev_12 => ({
              ...prev_12,
              seo: {
                ...prev_12.seo,
                fbAppId: e_7.target.value.replace(/\D/g, '')
              }
            }))} label={t('dashboard.settings.custom_domains.app_id')} error={errors.seo.fbAppId} />
            </div>
          </div>
          <div className="flex justify-center">
            <PrimaryButton width="large" onClick={(e_8: React.MouseEvent<HTMLButtonElement>) => {
            e_8.preventDefault();
            onEdit();
          }} isFetching={isFetching} type="submit">
              {t('global.save')}
            </PrimaryButton>
          </div>
          {errors.common && <p dangerouslySetInnerHTML={{
          __html: errors.common
        }} className="text-danger [&>a]:primary-link mt-2 text-center text-sm" />}
        </form>
      </Modal>
      <ConfirmationModal confirmationText={t('dashboard.settings.custom_domains.login_delete_confirmation')} title={t('confirmation_modal.default_title')} opened={isDeleteLoginOpen} onClose={() => setIsDeleteLoginOpen(false)} onConfirm={() => onRemoveLoginPage(domain)} />
      <ConfirmationModal confirmationText={t('dashboard.settings.custom_domains.not_found_delete_confirmation')} title={t('confirmation_modal.default_title')} opened={isDeleteNotFoundOpen} onClose={() => setIsDeleteNotFoundOpen(false)} onConfirm={() => onRemoveNotFoundPage(domain)} />
    </>;
};
export default DomainSettingsModal;